import { useState } from "react";
import { Plus, UserPlus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { getUserDisplayName, UserAvatar } from "@/components/ui/user-avatar";
import { cn } from "@/lib/utils";
import type { TemplateFormTranslations } from "@/lib/i18n/types/documents-sections/templateForm";
import type { UserProfile } from "@/domains/pm/types";
import { createPerson, type TemplateFormPerson } from "../../utils/templateForm";

/** The shared derivation, so a picked row reads the same as the avatar's own
 *  label and as every other member list in the app. */
const memberDisplayName = (user: UserProfile): string => getUserDisplayName(user);

/**
 * The project's members plus how their fetch is doing — the picker shows a
 * loading line and an error-with-retry instead of passing all three states
 * off as "no members found".
 */
export interface TemplateFormMembers {
  list: UserProfile[];
  /** `noProject` — the form has no project scope (building view), so there is
   *  no member list to fetch; the picker says so rather than passing it off as
   *  "no members found". The name inputs still take anything typed. */
  status: "loading" | "error" | "ready" | "noProject";
  retry: () => void;
}

/** Certificate number is only asked for on the acts that require it. */
function columnClasses(withCertificate: boolean): string {
  return cn(
    "grid items-center gap-2",
    withCertificate
      ? "sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_10rem_auto_auto]"
      : "sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto_auto]"
  );
}

interface PersonRowsFieldProps {
  copy: TemplateFormTranslations;
  people: TemplateFormPerson[];
  members: TemplateFormMembers;
  withCertificate: boolean;
  onChange: (people: TemplateFormPerson[]) => void;
}

/** The parties block: one row per person, each able to be marked as a signer. */
export function PersonRowsField({
  copy,
  people,
  members,
  withCertificate,
  onChange,
}: PersonRowsFieldProps) {
  const columns = columnClasses(withCertificate);

  return (
    <div className="space-y-2">
      <div className={cn(columns, "hidden text-xs text-muted-foreground sm:grid")}>
        <span>{copy.personName}</span>
        <span>{copy.personCompany}</span>
        {withCertificate && <span>{copy.personCertificate}</span>}
        <span>{copy.personSigner}</span>
        <span className="sr-only">{copy.removePerson}</span>
      </div>

      {people.map((person) => (
        <PersonRow
          key={person.rowId}
          copy={copy}
          person={person}
          members={members}
          withCertificate={withCertificate}
          onChange={(patch) =>
            onChange(people.map((row) => (row.rowId === person.rowId ? { ...row, ...patch } : row)))
          }
          onRemove={() => onChange(people.filter((row) => row.rowId !== person.rowId))}
        />
      ))}

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1.5"
        onClick={() => onChange([...people, createPerson()])}
      >
        <Plus aria-hidden className="h-4 w-4" />
        {copy.addPerson}
      </Button>
    </div>
  );
}

interface PersonRowProps {
  copy: TemplateFormTranslations;
  person: TemplateFormPerson;
  members: TemplateFormMembers;
  withCertificate: boolean;
  onChange: (patch: Partial<TemplateFormPerson>) => void;
  onRemove: () => void;
}

function PersonRow({ copy, person, members, withCertificate, onChange, onRemove }: PersonRowProps) {
  return (
    <div className={columnClasses(withCertificate)}>
      <PersonNameCell copy={copy} person={person} members={members} onChange={onChange} />
      <Input
        value={person.company}
        aria-label={copy.personCompany}
        onChange={(event) => onChange({ company: event.target.value })}
      />
      {withCertificate && (
        <Input
          value={person.certificate}
          aria-label={copy.personCertificate}
          onChange={(event) => onChange({ certificate: event.target.value })}
        />
      )}
      <div className="flex items-center gap-2">
        <Switch
          checked={person.signer}
          aria-label={copy.personSigner}
          onCheckedChange={(checked) => onChange({ signer: checked })}
        />
        <Label className="font-normal sm:hidden">{copy.personSigner}</Label>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={copy.removePerson}
        onClick={onRemove}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}

type PersonNameCellProps = Pick<PersonRowProps, "copy" | "person" | "members" | "onChange">;

/** The name field and the member picker that fills it in. */
function PersonNameCell({ copy, person, members, onChange }: PersonNameCellProps) {
  return (
    <div className="flex items-center gap-1">
      <Input
        value={person.name}
        aria-label={copy.personName}
        onChange={(event) => onChange({ name: event.target.value, userId: null })}
      />
      <MemberPicker
        copy={copy}
        members={members}
        onPick={(member) => onChange({ name: memberDisplayName(member), userId: member.id })}
      />
    </div>
  );
}

interface MemberPickerProps {
  copy: TemplateFormTranslations;
  members: TemplateFormMembers;
  onPick: (member: UserProfile) => void;
}

/** Fills a person row from the project's members, without locking the field:
 *  the name stays a plain input for people who are not Franklin users. */
function MemberPicker({ copy, members, onPick }: MemberPickerProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="ghost" size="icon" aria-label={copy.pickMember}>
          <UserPlus className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        {members.status === "ready" ? (
          <MemberList
            copy={copy}
            members={members.list}
            onPick={(member) => {
              onPick(member);
              setOpen(false);
            }}
          />
        ) : (
          <MemberPickerStatus copy={copy} members={members} />
        )}
      </PopoverContent>
    </Popover>
  );
}

/** Loading, no-project, or the failed fetch with its retry — never silently
 *  empty. */
function MemberPickerStatus({
  copy,
  members,
}: {
  copy: TemplateFormTranslations;
  members: TemplateFormMembers;
}) {
  return (
    <div className="px-3 py-6 text-center text-sm text-muted-foreground">
      {members.status === "loading" ? (
        copy.loadingMembers
      ) : members.status === "noProject" ? (
        copy.membersNeedProject
      ) : (
        <>
          <p>{copy.membersError}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={members.retry}
          >
            {copy.retryMembers}
          </Button>
        </>
      )}
    </div>
  );
}

function MemberList({
  copy,
  members,
  onPick,
}: {
  copy: TemplateFormTranslations;
  members: UserProfile[];
  onPick: (member: UserProfile) => void;
}) {
  return (
    <Command>
      <CommandInput placeholder={copy.searchMembers} />
      <CommandList>
        <CommandEmpty>{copy.noMembers}</CommandEmpty>
        <CommandGroup>
          {members.map((member) => (
            <CommandItem
              key={member.id}
              // The id keeps the value unique — cmdk resolves the highlight
              // by value, and two members can share a display name.
              value={`${memberDisplayName(member)} ${member.id}`}
              onSelect={() => onPick(member)}
              className="gap-2"
            >
              {/* The picture is the fast way to find a colleague in a long
                  project; the name still carries the accessible label, since
                  UserAvatar's own alt text would duplicate it. */}
              <UserAvatar user={member} size={22} />
              <span className="truncate">{memberDisplayName(member)}</span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );
}
